/**
 * 첫 유입 경로 (firstTouch · 2026-10-13 SEO 1단계) — 처음 들어온 방문의 UTM·참조 주소·첫 페이지를 1st-party 쿠키 `mt_ft` (90일) 에 한 번만 저장하고,
 * 가입할 때 users/{uid}.firstTouch 로 옮긴다 (이메일 가입·구글: auth-context.tsx · 카카오: /api/auth/kakao/callback 이 같은 쿠키를 읽는다).
 * 개인 식별 정보 0 (UTM 5개 · 참조 도메인+경로 · 첫 경로 · 시각) · 값마다 길이 제한 · 형식이 다르면 버린다.
 */
export const FIRST_TOUCH_COOKIE = "mt_ft";
export const FIRST_TOUCH_MAX_AGE_S = 90 * 24 * 60 * 60;
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;
const MAX_LEN = 120;

export interface FirstTouch {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  /** 참조 주소 (외부 사이트만 · 쿼리 제거 · 같은 사이트·없음은 null). */
  referrer: string | null;
  /** 처음 연 경로 (쿼리 제거). */
  landingPath: string;
  /** 처음 연 시각 (ISO · 브라우저 시각). */
  at: string;
}

const clip = (v: string | null | undefined): string | null => {
  const s = (v ?? "").trim();
  return s ? s.slice(0, MAX_LEN) : null;
};

/** 현재 주소·참조 주소로 firstTouch 만들기 (순수 함수 · 단위 테스트). */
export function buildFirstTouch(href: string, referrer: string, nowIso: string): FirstTouch {
  const u = new URL(href);
  let ref: string | null = null;
  try {
    if (referrer) {
      const r = new URL(referrer);
      if (r.host !== u.host) ref = clip(`${r.host}${r.pathname === "/" ? "" : r.pathname}`);
    }
  } catch {
    ref = null;
  }
  const utm = Object.fromEntries(UTM_KEYS.map((k) => [k, clip(u.searchParams.get(k))])) as Pick<FirstTouch, (typeof UTM_KEYS)[number]>;
  return { ...utm, referrer: ref, landingPath: clip(u.pathname) ?? "/", at: nowIso };
}

/** 쿠키 값 → firstTouch (형식 검사 · 서버·브라우저 공용). 이상하면 null. */
export function parseFirstTouch(raw: string | null | undefined): FirstTouch | null {
  if (!raw) return null;
  try {
    // 서버 (Next cookies) 는 이미 풀린 값 · 브라우저 document.cookie 는 인코딩된 값 — 둘 다 받는다
    const o = (raw.trimStart().startsWith("{") ? JSON.parse(raw) : JSON.parse(decodeURIComponent(raw))) as Record<string, unknown>;
    const str = (k: string) => (typeof o[k] === "string" ? clip(o[k] as string) : null);
    const at = str("at");
    if (!at || Number.isNaN(Date.parse(at))) return null;
    return {
      utm_source: str("utm_source"), utm_medium: str("utm_medium"), utm_campaign: str("utm_campaign"), utm_term: str("utm_term"), utm_content: str("utm_content"),
      referrer: str("referrer"), landingPath: str("landingPath") ?? "/", at,
    };
  } catch {
    return null;
  }
}

export function serializeFirstTouch(ft: FirstTouch): string {
  return encodeURIComponent(JSON.stringify(ft));
}

/** 브라우저: 쿠키에서 읽기 (없으면 null). */
export function readFirstTouchCookie(): FirstTouch | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.split("; ").find((c) => c.startsWith(`${FIRST_TOUCH_COOKIE}=`));
  return parseFirstTouch(m ? m.slice(FIRST_TOUCH_COOKIE.length + 1) : null);
}

/** 브라우저: 쿠키가 없을 때만 저장 (처음 한 번). */
export function captureFirstTouch(): void {
  if (typeof document === "undefined" || readFirstTouchCookie()) return;
  const ft = buildFirstTouch(window.location.href, document.referrer, new Date().toISOString());
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${FIRST_TOUCH_COOKIE}=${serializeFirstTouch(ft)}; Max-Age=${FIRST_TOUCH_MAX_AGE_S}; Path=/; SameSite=Lax${secure}`;
}
